import React, { Component, ErrorInfo, ReactNode } from "react";
import { captureError } from "@/lib/observability";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  sectionName?: string;
  /** Render a friendly inline card with a retry action when no fallback is supplied. */
  retryable?: boolean;
}

interface State {
  hasError: boolean;
}

/**
 * Lightweight error boundary for dashboard sections.
 * Prevents a single section crash from taking down the entire page.
 *
 * A crash is reported once per mount — a crashing subtree must never be able
 * to produce an error storm (see the job detail remount loop, Sept 2026).
 */
export class SectionErrorBoundary extends Component<Props, State> {
  private reported = false;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (this.reported) return;
    this.reported = true;
    console.error(`[SectionErrorBoundary] ${this.props.sectionName || "Unknown"} crashed:`, error, errorInfo);
    captureError(`react.section.${this.props.sectionName || "unknown"}`, error, {
      metadata: { componentStack: errorInfo.componentStack?.slice(0, 300) },
    });
  }

  private retry = () => {
    this.reported = false;
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      if (!this.props.retryable) return null;
      return (
        <div className="rounded-lg border bg-card p-6 text-center">
          <AlertCircle className="mx-auto h-6 w-6 text-muted-foreground" />
          <p className="mt-2 font-medium text-foreground">This section couldn't be displayed</p>
          <p className="mt-1 text-sm text-muted-foreground">The rest of this job is still available.</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={this.retry}>Try again</Button>
        </div>
      );
    }
    return this.props.children;
  }
}
